import { useEffect, useMemo, useState } from 'react';
import { Controller, FieldErrors, FormProvider, Path, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Listbox } from '@components/listbox';
import { ModalWithLayout } from '@components/modals/modal-with-layout';
import { TextareaCounter } from '@components/textarea-counter';
import { Translations } from '@domain/admission';
import { LANGUAGES } from '@domain/language';
import { getDynamicPlaceholder } from '@utils/get-dynamic-placeholder/get-dynamic-placeholder';
import classNames from 'classnames';

import { EditFieldModalProps, Form, OFFER_NAME_MAX_LENGTH } from './edit-field-modal.types';

export const EditFieldModal = ({
  isOpen,
  onClose,
  onSave,
  defaultValue,
  isEditDisabled,
  labels,
  maxLength = OFFER_NAME_MAX_LENGTH,
  placeholder,
  validation,
  disableButtonWithEmptyField = false,
  helperText,
  inputLabel,
  subtitle,
}: EditFieldModalProps) => {
  const { t, i18n } = useTranslation();

  const [selectedLanguage, setSelectedLanguage] = useState(i18n.language);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const methods = useForm<Form>({
    defaultValues: {
      nameLanguage: i18n.language,
      name: defaultValue,
    },
    mode: 'onChange',
  });

  const {
    register,
    watch,
    control,
    formState: { errors, isValid, isDirty },
    handleSubmit,
    reset,
  } = methods;

  // Reset form when modal opens/closes or defaultValue changes
  useEffect(() => {
    if (isOpen) {
      reset({
        nameLanguage: i18n.language,
        name: defaultValue,
      });
      setSelectedLanguage(i18n.language);
    }
  }, [isOpen, defaultValue, i18n.language, reset]);

  const onSubmit: SubmitHandler<Form> = async (form) => {
    onSave(form.name);
  };

  const watchedName = watch('name');

  const isDirtyCheck = defaultValue ? !isDirty : false;

  const isAnyLanguageFilled = Object.values(watchedName || {}).some((v) => !!v?.trim());

  const isSubmitButtonDisabled = isDirtyCheck || !isValid || (!isAnyLanguageFilled && disableButtonWithEmptyField);

  const languageOptions = useMemo(
    () =>
      LANGUAGES.map(({ id, value }) => ({
        id,
        value: t(value),
      })),
    [t]
  );

  const handleClose = () => {
    onClose();
  };

  return (
    <ModalWithLayout
      isOpen={isOpen}
      onClose={handleClose}
      variant='md'
      title={labels?.title ? t(labels.title) : t('offers:admissionOfferNameLabel')}
      subtitle={subtitle}
      isForm
      onSubmit={handleSubmit(onSubmit)}
      footer={{
        primaryButtonText: labels?.saveButton ? t(labels.saveButton) : t('common:save'),
        isSubmit: true,
        isPrimaryDisabled: isSubmitButtonDisabled,
        primaryDataTestId: 'edit-field-modal--save-button',
      }}>
      <FormProvider {...methods}>
        <div className='flex justify-end'>
          <div className={classNames('text-body-xs content-center', { 'flex justify-between w-full': !!inputLabel })}>
            {!!inputLabel && (
              <label
                htmlFor='nameLanguage'
                className={classNames('', {
                  'text-critical-no-bg': !isValid,
                  'text-secondary': isValid && !isInputFocused,
                  'text-primary': isInputFocused && isValid,
                })}>
                {inputLabel}
              </label>
            )}
            <div className='w-36'>
              <Controller
                name='nameLanguage'
                control={control}
                render={({ field }) => {
                  const currentError = (errors.name as FieldErrors<Translations>)?.[selectedLanguage];
                  const hasValue = !!watchedName?.[selectedLanguage]?.trim();
                  const isLanguageDisabled = !!(currentError && currentError.type !== 'required' && hasValue);

                  return (
                    <Listbox
                      {...field}
                      onChange={(value) => {
                        field.onChange(value);
                        setSelectedLanguage(value as string);
                      }}
                      options={languageOptions}
                      zIndexClassName='z-[9999]'
                      disabled={isLanguageDisabled}
                    />
                  );
                }}
              />
            </div>
          </div>
        </div>
        {LANGUAGES.map((lang) => {
          const name = `name.${lang.id}`;

          const error = (errors.name as FieldErrors<Translations>)?.[lang.id];
          const hasVisibleError = !!error;

          const nameField = watch(name as Path<Form>) as string;
          const length = nameField?.length || 0;

          const isSelected = lang.id === selectedLanguage;

          const textAreaRegister = register(name as Path<Form>, {
            maxLength,
            required: isSelected && validation?.required,
            pattern: validation?.pattern,
            validate: (value) =>
              validation?.validate ? validation.validate(value as string, lang.id, watchedName) : true,
          });

          return (
            <div key={`name-${lang.id}`} className={classNames({ hidden: !isSelected })}>
              <div className={'mt-1'}>
                <TextareaCounter
                  id={name}
                  rows={3}
                  data-testid='edit-field-modal--text-area'
                  placeholder={
                    placeholder ?? getDynamicPlaceholder(t('offers:admissionOfferNamePlaceholder'), defaultValue)
                  }
                  length={length}
                  maxLength={maxLength}
                  hasError={hasVisibleError}
                  charactersText={t('common:characters')}
                  disabled={isEditDisabled}
                  readOnly={isEditDisabled}
                  {...textAreaRegister}
                  onFocus={() => setIsInputFocused(true)}
                  onBlur={() => setIsInputFocused(false)}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => {
                    // Physically limit input to maxLength by truncating
                    const truncatedValue = e.target.value.slice(0, maxLength);
                    if (e.target.value !== truncatedValue) {
                      e.target.value = truncatedValue;
                    }

                    textAreaRegister.onChange(e);
                  }}
                  helperText={error?.message ?? helperText}
                />
              </div>
            </div>
          );
        })}
      </FormProvider>
    </ModalWithLayout>
  );
};
